import type { AiCapabilityPage } from "../schema.js";
import { ALL_ROLES, LIVE, SLOT_GROUP } from "./_shared.js";

/**
 * `READ-COLLECTIONS` (@UC0206) — đọc ĐỢT THU: đợt nào đang mở · tiến độ · ai chưa đóng. Tier `read`: không phiếu, chạy ngay.
 * Số CHÈN SAU LLM từ fact block (`@UC0154`). Executor `collectionRead.status` là **dispatcher** theo ô `chiSo`, nối tới ĐÚNG
 * hai cửa của màn Đợt thu: `FundCollectionProgressService.listByGroup` (đang mở · tiến độ) · `MembersPaymentStatusService.get`
 * (ai chưa đóng). Cùng hàm với màn hình, không truy vấn riêng.
 *
 * 🔴 Danh sách "chưa đóng" tuân `payment_status_visibility` fund > group > system — chat đọc CÙNG khoá với form, không cửa hậu
 * (P5.3, QĐ PO 06/08/2026). Mặc định hệ thống `all_members` (QĐ người dùng 07/09/2026, `fund-defaults.ts`); nhóm chọn
 * `managers_only` thì member thường KHÔNG thấy tên. Đây là **kênh XEM**; kênh NHẮC (đòi nợ) riêng tư tuyệt đối, không cấu
 * hình được, và trang này KHÔNG chạm tới nó.
 *
 * 🔴 Ô `nhom` thêm ở 0045 (cùng ca PO đã chốt cho `COL-EQUAL` 17/09 16:05): tab trợ lý là tab TOÀN CỤC, nên phiên mở ở scope
 * "tất cả nhóm" có `session.groupId = null`. Trang không groupless và không có ô `group` ⇒ `assertCanOpen` 403
 * `AI_GROUP_REQUIRED` **và** `allowedPages` lọc nó khỏi menu — trang có mặt trong Sổ mà không đường nào tới.
 *
 * 🔴 `tenDot` KHÔNG còn là fact (nháp 0013 có). Chỉ số `đang mở` của CHÍNH trang này trả một DANH SÁCH đợt — không có "tên
 * đợt" nào để điền, mà `renderTemplate` ném khi chỗ trống rỗng. Giữ nó là tự cài một ca 500 chắc chắn cho 1/3 đường đi của
 * trang, **sau khi** đã tốn truy vấn. `tieuDe` thay chỗ: chỉ số nào cũng có một chủ đề nói được thành chữ.
 */
/** Lời khai chung của hai câu C06(a) (±dấu) — một quyết định, hai cách gõ. */
const ALSO_ACCEPT_C06A = {
  reason: "câu HỎI ai đã báo chuyển có hai cách xử lý đúng: đọc tình trạng đợt thu, hoặc mở DANH SÁCH lời báo đang chờ (0 phiếu ghi)",
  decision: "QĐ điều phối 29/09 theo AC C06(a) US-AIV2-0111 — trả lời đọc / mở lời báo chờ",
  decidedOn: "2026-09-29",
} as const;

export const READ_COLLECTIONS = {
  id: "READ-COLLECTIONS",
  uc: "@UC0206",
  tier: "read",
  status: LIVE,
  roles: ALL_ROLES,
  flags: [],
  slots: [
    // Ô ĐẦU theo bậc phạm vi: nhóm chốt trước, rồi mới tới "xem gì" và "đợt nào".
    SLOT_GROUP,
    {
      id: "chiSo",
      type: "enum",
      required: true,
      ask: "Xem đợt nào đang mở, tiến độ thu, hay ai chưa đóng?",
      clarify: "Bạn muốn xem đợt đang mở, tiến độ, hay danh sách chưa đóng?",
      choices: ["đang mở", "tiến độ", "chưa đóng"],
      /*
       * 🔴 US-AIV2-0111 C10 (@UC0206) — đo 28/09: "thu được bao nhiêu rồi", "được mấy bạn đóng rồi", "ai chưa nộp quỹ", "còn thiếu"
       * vào ô này mà không chứa chữ một lựa chọn ⇒ hỏi lại. Khớp theo TỪ, bỏ dấu; cụm dài nhất thắng. Chữ "chưa đóng" của chính
       * lựa chọn vẫn đi trước ("còn thiếu nhà nào chưa đóng" ⇒ chưa đóng mà không cần đồng nghĩa).
       */
      synonyms: {
        "đang mở": ["đợt nào", "đang có", "đang thu", "mở chưa", "những đợt"],
        "tiến độ": ["thu được", "được bao nhiêu", "thu được bao nhiêu", "đủ chưa", "thu đủ", "mấy bạn đóng", "mấy người đóng", "bao nhiêu người đóng", "bao nhiêu người đã đóng", "đóng rồi", "tới đâu", "đến đâu", "bao nhiêu phần trăm", "mấy phần", "tình hình"],
        "chưa đóng": ["báo đã chuyển", "đã báo chuyển", "báo chuyển", "chưa nộp", "chưa chuyển", "chưa trả", "còn thiếu", "thiếu", "ai chưa", "nhà nào chưa", "bé nào chưa", "con nào chưa", "bạn nào chưa", "còn nợ"],
      },
      // Mơ hồ ⇒ tiến độ: đã thu / cần thu (%) + bao nhiêu người đã đóng — một câu trả lời cho cả "thu được bao nhiêu" lẫn "đủ chưa".
      fallback: "tiến độ",
    },
    // KHÔNG `autofill` (review #825 M3): trang ĐỌC hứa "bỏ trống = đợt đang mở gần nhất" — ≥2 đợt vẫn trả lời ngay, 0 đợt thì executor nói
    // "chưa có đợt nào đang mở" (đó là CÂU TRẢ LỜI của câu hỏi "đợt nào đang mở?"), không huỷ phiếu đi mời mở đợt thu.
    { id: "dot", type: "collection", required: false, ask: "Đợt thu nào? (bỏ trống = đợt đang mở gần nhất)" },
  ],
  llm: {
    /*
     * 🔴 US-AIV2-0110 S11 — HAI thay đổi, đừng gộp khi đọc diff:
     *  1. (@UC0273) thêm ranh giới *"nhóm này có bao nhiêu người"* ⇒ READ-PEOPLE-MINE — câu `known-intent-gaps` model chọn nhầm
     *     trang này (conf 0,2, đo 24/09). READ-PEOPLE-MINE mọi vai đều thấy ⇒ nhắc id của nó ở đây không rò gì.
     *  2. (@UC0152) GỠ id `DEBT-REMIND` khỏi mô tả. Trang này MỌI VAI thấy, còn DEBT-REMIND là trang GHI chỉ-quản-lý ⇒ id của nó
     *     đi vào prompt của THÀNH VIÊN mỗi khi trang này lọt top-8: đo trên develop (S20 base) **1.232/2.076** câu ví dụ, vai
     *     member. Ca `@UC0152` ở api chỉ đo MỘT câu nên không thấy; thêm vế 1 kéo trang này vào top-8 của đúng câu đó và ca đỏ.
     *     Ranh giới theo ĐỘNG TỪ vẫn nói đủ bằng lời; ranh giới bằng id đã có sẵn ở chiều kia (`debt-remind.ts` nhắc
     *     READ-COLLECTIONS) — người quản lý thấy cả hai trang nên vẫn đọc được nó.
     */
    description:
      "XEM (chỉ đọc, không gửi đi đâu) tình hình đợt thu: đợt nào đang mở, thu tới đâu rồi, những ai còn chưa đóng. Ranh giới với việc GỬI LỜI NHẮC nằm ở ĐỘNG TỪ, không ở chữ 'nợ'/'chưa đóng': câu chỉ HỎI/LIỆT KÊ (\"ai chưa đóng\", \"còn ai chưa nộp\", \"mấy bạn còn nợ đợt thu\") là XEM ở đây, kể cả khi có chữ 'nợ'; câu SAI KHIẾN GỬI ĐI (\"nhắc/giục/ping/đòi người chưa đóng\") KHÔNG ở đây. Đếm NGƯỜI của cả nhóm (\"nhóm này có bao nhiêu người\") KHÔNG ở đây mà là READ-PEOPLE-MINE — ở đây chỉ đếm người trong MỘT ĐỢT THU (bao nhiêu người đã đóng, còn ai chưa đóng). Danh sách 'chưa đóng' tuân `payment_status_visibility` của nhóm (fund > group > system; mặc định hệ thống hiện `all_members` — QĐ 07/09/2026, nhóm đổi được) — chat đọc cùng khoá với màn hình, không cửa hậu.",
    examples: [
      "ai chưa đóng tháng này?",
      "ai chua dong thang nay",
      "đợt thu nào đang mở?",
      "dot thu nao dang mo",
      "quỹ thu tới đâu rồi",
      "quy thu toi dau roi",
      "ai chưa đóng",
      "tiến độ đợt thu dã ngoại",
      "còn mấy người chưa nộp",
      "thu được bao nhiêu rồi",
      "ai còn thiếu",
      // ——— 0105: thêm cho đủ 40 ———
      "danh sách người chưa nộp quỹ",
      "danh sach nguoi chua nop quy",
      "đợt thu tháng này thu được mấy phần rồi",
      "dot thu thang nay thu duoc may phan roi",
      "nhóm đang có đợt thu nào không",
      "nhom dang co dot thu nao khong",
      "xem tiến độ thu quỹ lớp",
      "xem tien do thu quy lop",
      "còn ai chưa chuyển tiền không",
      "con ai chua chuyen tien khong",
      "bao nhiêu người đã đóng rồi",
      "bao nhieu nguoi da dong roi",
      "đợt thu quỹ đang mở tới ngày nào",
      "dot thu quy dang mo toi ngay nao",
      "kiểm tra tình hình thu tiền",
      "kiem tra tinh hinh thu tien",
      /*
       * 🔴 0105 vòng 2 — BA CẶP CÂU bị THAY, không phải bị thêm. Cả ba do chính vòng 1 đưa vào để
       * "cho đủ 40", và đo được trên bản ghi thật chúng là thứ kéo trang từ 95% xuống **88,10%**:
       *
       *   · `"ai chua dong dot da ngoai"` → `DEBT-REMIND` · `"mấy bạn còn nợ đợt thu"` + bản không
       *     dấu → `DEBT-REMIND` (3 ca). Chữ **"nợ"** và cụm *"chưa đóng đợt <tên>"* nghiêng hẳn sang
       *     việc ĐÒI. Đây là ranh giới THẬT giữa hai trang, nên vế mô tả cũng được siết (xem trên) —
       *     nhưng một câu mẫu đứng **sai bên** ranh giới thì không mô tả nào cứu nổi: nó đang DẠY
       *     model rằng câu ấy thuộc về đây.
       *   · `"danh sách đóng tiền của đợt này"` + bản không dấu → model trả `page` = **chính chuỗi
       *     câu mẫu** (`"danh sách đóng tiền của đợt này"`), trượt `AI_PAGE_ID_RE` ⇒ 2 ca
       *     `envelope_invalid`. Câu mẫu trùng khít một cụm danh từ có thể đọc như **tên một thứ**,
       *     và model chép nó vào ô id thay vì chọn trang.
       *
       * ⚠️ KHÔNG hạ sàn ca: ba cặp bị bỏ được thay bằng ba cặp khác, trang giữ nguyên 42 ca.
       */
      "còn ai chưa nộp cho đợt dã ngoại không",
      "con ai chua nop cho dot da ngoai khong",
      "liệt kê các lần thu đang mở",
      "liet ke cac lan thu dang mo",
      "thu quỹ được bao nhiêu phần trăm",
      "thu quy duoc bao nhieu phan tram",
      "xem bảng đóng tiền của đợt này",
      "xem bang dong tien cua dot nay",
      "đợt thu này bao nhiêu người đóng rồi",
      "dot thu nay bao nhieu nguoi dong roi",
      "đợt thu nào chưa xong",
      "dot thu nao chua xong",
      "tình hình đợt thu hiện tại",
      "tinh hinh dot thu hien tai",
      /*
       * 🔴 US-AIV2-0111 C06 (@UC0320 @UC0206) — câu HỎI "ai đã báo chuyển" là XEM ở đây (chỉ số `chưa đóng` nêu ai đã báo,
       * đang chờ đối chiếu — §3.7 trạng thái 2 — ở nhóm RIÊNG, không đếm vào số "chưa đóng": AI2D-T13 @UC0448), KHÔNG mở phiếu tự báo PAY-REPORT. Đo 28/09: chủ nhóm FC hỏi đúng câu này
       * ⇒ phiếu "vào sổ quỹ ngay" cho chính mình (3/3). Câu này cũng là `questionsNotHere` của PAY-REPORT/PAY-CONFIRM-DECIDE.
       */
      "có ai báo đã chuyển tiền chưa",
      "co ai bao da chuyen tien chua",
    ],
    // AI-INTERP M12 (QĐ I12): câu THỬ — KHÔNG vào prompt/BM25; viết đời thường, không chép ví dụ (validator HELD_OUT_IS_EXAMPLE).
    heldOutExamples: [
      "dot thu nay gom dc bao nhieu roi",
      "còn nhà nào chưa góp không chị",
      "ai chua ck tien ao doi vay",
      "đợt thu tháng này xong chưa ạ",
      "dang co dot thu nao ko a",
      "lop minh dong dc may nha roi",
    ],
    /*
     * 🔴 US-AIV2-0105 — DANH TỪ TRẦN `"đợt thu"` chuyển sang đây. Đo 24/09: model trả `unknown`
     * (conf 0,2), đúng. Hai chữ này ứng được với ÍT NHẤT bốn trang — đo bằng phép giống nhau giữa các
     * trang: `COL-EQUAL "mở đợt thu"` · `COL-EDIT "sửa đợt thu"` · `COL-CLOSE-REOPEN "chốt đợt thu"` ·
     * chính trang này. Người gõ hai chữ đó chưa nói mình muốn XEM, MỞ, SỬA hay CHỐT.
     */
    clarifyExamples: ["đợt thu"],
  },
  /*
   * 🔴 US-AIV2-0114 L09 (@UC0176 @UC0320) — ĐÁP ÁN PHỤ của bộ eval (chuyển từ hằng `DAP_AN_CHAP_NHAN` của `known-intent-gaps.test.ts`,
   * nay cổng đọc được). NGUỒN: QĐ điều phối 29/09 theo AC C06(a) "trả lời đọc / MỞ LỜI BÁO CHỜ, 0 phiếu ghi" — câu HỎI ai đã báo
   * chuyển có HAI cách xử lý đúng: đọc ở đây, hoặc PAY-CONFIRM-DECIDE mở DANH SÁCH lời báo chờ (đo #1018: model chọn nó conf 0,42).
   * ĐIỀU KIỆN của QĐ (máy giữ — luật `EVAL_ALSO_ACCEPT_WRITE_FOR_READ` ở `validate.ts`, review #1134): PAY-CONFIRM-DECIDE hỏi NGƯỜI trước
   * quyết định (`askAfter: "nguoi"`) và 0 lời báo ⇒ câu đọc (`emptyByState`) — tức người dùng THẤY danh sách trước mọi nút ghi (int-spec
   * `ai-doer-pay-confirm-offer`).
   * 🔁 Gỡ khi: ≥3 lượt thu liên tiếp model chọn trang này cho cả hai bản; HOẶC PAY-CONFIRM-DECIDE thôi `askAfter`/`emptyByState`
   * (lúc đó nó KHÔNG còn là đáp án đúng — ca này phải thành khoảng trống thật).
   */
  evalAlsoAccept: {
    "có ai báo đã chuyển tiền chưa": { pages: ["PAY-CONFIRM-DECIDE"], ...ALSO_ACCEPT_C06A },
    "co ai bao da chuyen tien chua": { pages: ["PAY-CONFIRM-DECIDE"], ...ALSO_ACCEPT_C06A },
  },
  executor: "collectionRead.status",
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: bốn cửa đọc tình trạng đợt thu (bảng đóng tiền theo vai — `payment_status_visibility`). */
  endpoints: [
    "GET /collections/{collectionId}",
    "GET /collections/{collectionId}/payment-board",
    "GET /funds/{fundId}/collection-progress",
    "GET /groups/{groupId}/collection-progress",
  ],
  facts: ["tieuDe", "tomTat"],
  templates: {
    // 🔴 CHỈ fact được đứng ở đây: `AiDoerService` gọi `renderReply(page, view, out.result, pages)` và `out.result` là facts
    // của executor — nhãn ô KHÔNG có mặt. Một `{nhom}` ở đây ném `TEMPLATE_PLACEHOLDER_MISSING` ở CUỐI lượt đã đọc xong.
    reply: "{tieuDe}: {tomTat}.",
  },
  /**
   * US-AIV2-tourguide-0005 (@UC0364) — kết quả của binding. Máy đo K03: nhóm 0 đợt / 0 đợt mở ⇒ KHÔNG «Nhắc người chưa đóng» lẫn «Ghi nhận đã
   * đóng» (cả hai bấm vào là «Chưa có đợt thu nào đang mở.»). Mọi người đã đóng đủ ⇒ không mời nhắc/ghi. Danh sách tên bị ẩn (thành viên ở
   * nhóm `managers_only`) ⇒ không nút (hai trang kia vốn chỉ quản lý mở được).
   */
  journey: {
    outcomes: {
      // review 0005 MED-2: chưa có đợt / không đợt nào mở ⇒ mời «Mở đợt thu» (COL-EQUAL) — cổng quyền chỉ cho chủ nhóm/quản trị/thủ quỹ,
      // `ready()` giấu khi người này không có quỹ mở đợt được. Vẫn KHÔNG «Nhắc»/«Ghi nhận» (K03).
      no_collection: { next: ["COL-EQUAL"] },
      no_open: { next: ["COL-EQUAL"] },
      open_list: { next: ["DEBT-REMIND", "RECORD-PAID"] },
      has_unpaid: { next: ["DEBT-REMIND", "RECORD-PAID"] },
      // UX-SMALL-WORDING (V-5 · @UC0322): câu ĐÃ nói tiến độ đợt (còn người chưa đóng) ⇒ cùng nút + phủ thẻ tiến độ — CHỈ khi thẻ trỏ CÙNG đợt (binding khai
      // `__subject`, luật ở `attachGuideLine`): giữ nút «Nhắc người chưa đóng», bỏ câu thẻ nói lại tiến độ.
      progress_unpaid: { next: ["DEBT-REMIND", "RECORD-PAID"], covers: ["DUTY-COLLECTION-PROGRESS"] },
      all_paid: { next: [] },
      // review 0005 LOW-1: đợt 0 suất — không "đã đóng đủ", cũng không có ai để nhắc/ghi nhận ⇒ không mời gì.
      no_seats: { next: [] },
      names_hidden: { next: [] },
    },
  },
  help: { title: "Xem các đợt thu", tryNow: "ai chưa đóng tháng này?" },
} satisfies AiCapabilityPage;
